import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { takeUntil } from 'rxjs/operators';
import { Observable, Observer, Subject } from 'rxjs';

@Component({
  selector: 'aid-upload-photo',
  template: `
    <div
      class="full-height aid-layout-column aid-layout-align-space-between-center"
    >
      @if (!cropPhoto) {
        <div class="upload-file">
          <aid-upload-file
            [defaultUrl]="displayedUrl"
            [control]="control"
            [willCropPhoto]="true"
            [acceptFiles]="acceptedFiles"
            [details]="details"
          ></aid-upload-file>
        </div>
      } @else {
        <image-cropper
          class="image-cropper"
          [imageChangedEvent]="imageChangedEvent"
          [maintainAspectRatio]="true"
          [aspectRatio]="aspectRatio"
          [resizeToWidth]="horizontalSize"
          [resizeToHeight]="verticalSize"
          [onlyScaleDown]="true"
          [cropperMinWidth]="horizontalSize"
          [cropperMinHeight]="verticalSize"
          [roundCropper]="roundCropper"
          format="png"
          output="base64"
          (imageCropped)="imageCropped($event)"
          (loadImageFailed)="imageLoadFailed()"
          [alignImage]="'center'"
        ></image-cropper>
      }
    </div>
  `,
  styleUrls: ['./upload-photo.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UploadPhotoComponent implements OnInit, OnDestroy {
  @Input() displayedUrl = '';
  @Input() control: UntypedFormControl = new UntypedFormControl();
  @Input() willCropPhoto = false;
  @Input() horizontalSize = 0;
  @Input() verticalSize = 0;
  @Input() details = '';
  // Profile photos pass no accept filter, which clears this default.
  @Input() acceptedFiles: string | undefined = '.png,.jpg,jpeg';

  @Input() aspectRatio = 1;
  @Input() roundCropper = false;
  imageChangedEvent: Event | null = null;
  cropPhoto = false;
  private readonly destroyed = new Subject<void>();

  ngOnInit() {
    this.control.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((value: unknown) => {
        if (value === null) {
          this.cropPhoto = false;
          this.imageChangedEvent = null;
        } else if (
          value instanceof Event &&
          value.target instanceof HTMLInputElement &&
          value.target.files?.length
        ) {
          this.cropPhoto = true;
          this.imageChangedEvent = value;
        }
      });
  }

  imageCropped(event: ImageCroppedEvent) {
    if (!this.cropPhoto || !event.base64) {
      return;
    }
    this.dataURItoBlob(event.base64).subscribe({
      next: file => this.control.setValue(file),
      error: () => this.imageLoadFailed()
    });
  }

  imageLoadFailed(): void {
    this.cropPhoto = false;
    this.imageChangedEvent = null;
    this.control.setValue(null, { emitEvent: false });
    this.control.setErrors({ ...this.control.errors, image: true });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  dataURItoBlob(dataURI: string): Observable<File> {
    return new Observable((observer: Observer<File>) => {
      if (!/^data:image\/png;base64,/.test(dataURI)) {
        throw new Error('The cropper must emit a PNG data URL.');
      }
      const url = dataURI.split(',')[1];
      const byteString = window.atob(url);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([int8Array], { type: 'image/png' });
      const randomNumber: number = Math.random();
      const file: File = new File([blob], 'croppedImage' + randomNumber, {
        type: 'image/png',
        lastModified: Date.now()
      });
      observer.next(file);
      observer.complete();
    });
  }
}
